import {
  BehavioralDomainId,
  DomainMeta,
  TargetItem,
  ScheduledStrictnessRule,
  StrictnessTier,
} from './target-types';

export const BEHAVIORAL_DOMAINS: DomainMeta[] = [
  {
    id: 'financial_speculation',
    displayName: 'Financial & Speculation',
    shortName: 'Finance & Trading',
    description: 'High-risk crypto exchanges, options trading, and sports betting.',
    icon: '📈',
    accentColor: '#D97706',
    bgSoftColor: '#FEF3C7',
    defaultDaytimeTier: 'softPause',
    defaultNighttimeTier: 'hardLock',
    defaultMicroSession: 'urge_surfing',
  },
  {
    id: 'mindful_media',
    displayName: 'Social Media & Infinite Feeds',
    shortName: 'Social Media',
    description: 'Algorithmic social feeds, doomscrolling, and short-form video reels.',
    icon: '📱',
    accentColor: '#0284C7',
    bgSoftColor: '#E0F2FE',
    defaultDaytimeTier: 'softPause',
    defaultNighttimeTier: 'reflectiveJournal',
    defaultMicroSession: 'cbt_journaling',
  },
  {
    id: 'mindful_spending',
    displayName: 'Impulse Shopping & Flash Sales',
    shortName: 'Impulse Shopping',
    description: 'One-click checkout shopping, flash sales, and impulse carts.',
    icon: '🛍️',
    accentColor: '#059669',
    bgSoftColor: '#D1FAE5',
    defaultDaytimeTier: 'reflectiveJournal',
    defaultNighttimeTier: 'coolingOffContract',
    defaultMicroSession: 'cooling_off_contract',
  },
  {
    id: 'mindful_eating',
    displayName: 'Late-Night Food Delivery',
    shortName: 'Food Delivery',
    description: 'Late-night on-demand food delivery and impulsive binge cues.',
    icon: '🥗',
    accentColor: '#16A34A',
    bgSoftColor: '#DCFCE7',
    defaultDaytimeTier: 'softPause',
    defaultNighttimeTier: 'groundingExercise',
    defaultMicroSession: 'diaphragmatic_breathing',
  },
  {
    id: 'substance_reduction',
    displayName: 'Substance & Alcohol Reduction',
    shortName: 'Sobriety & Substance',
    description: 'Alcohol delivery, vape merchants, and impulse trigger services.',
    icon: '🛡️',
    accentColor: '#DC2626',
    bgSoftColor: '#FEE2E2',
    defaultDaytimeTier: 'groundingExercise',
    defaultNighttimeTier: 'hardLock',
    defaultMicroSession: 'substance_interruption',
  },
  {
    id: 'digital_dependency',
    displayName: 'Digital Dependency & Screen-Time',
    shortName: 'Screen-Time Balance',
    description: 'Compulsive device unlocking, video streaming, and late-night binge watching.',
    icon: '⏳',
    accentColor: '#7C3AED',
    bgSoftColor: '#EDE9FE',
    defaultDaytimeTier: 'softPause',
    defaultNighttimeTier: 'groundingExercise',
    defaultMicroSession: 'stretching_guide',
  },
  {
    id: 'adult_media',
    displayName: 'Adult Media & Explicit Content',
    shortName: 'Adult Media',
    description: 'Adult streaming platforms, subscription sites, and explicit habit loops.',
    icon: '🔞',
    accentColor: '#E11D48',
    bgSoftColor: '#FFE4E6',
    defaultDaytimeTier: 'reflectiveJournal',
    defaultNighttimeTier: 'hardLock',
    defaultMicroSession: 'cbt_journaling',
  },
  {
    id: 'gaming_microtransactions',
    displayName: 'Gaming & Microtransactions',
    shortName: 'Gaming & Lootboxes',
    description: 'Steam, Roblox, Twitch, Discord lootboxes, and compulsive in-game purchases.',
    icon: '🎮',
    accentColor: '#0D9488',
    bgSoftColor: '#CCFBF1',
    defaultDaytimeTier: 'softPause',
    defaultNighttimeTier: 'coolingOffContract',
    defaultMicroSession: 'cooling_off_contract',
  },
  {
    id: 'after_hours_work',
    displayName: 'After-Hours Work & Burnout',
    shortName: 'Work Boundaries',
    description: 'Slack, Work Gmail, Jira, Outlook, and LinkedIn after designated hours.',
    icon: '💼',
    accentColor: '#2D5A27',
    bgSoftColor: '#E8F5E9',
    defaultDaytimeTier: 'softPause',
    defaultNighttimeTier: 'groundingExercise',
    defaultMicroSession: 'stretching_guide',
  },
  {
    id: 'anxiety_news',
    displayName: 'Compulsive News & Panic Scrolling',
    shortName: 'Anxiety News',
    description: 'Breaking news cycles, alarmist headlines, and 24-hour cable feeds.',
    icon: '📰',
    accentColor: '#B45309',
    bgSoftColor: '#FEF3C7',
    defaultDaytimeTier: 'softPause',
    defaultNighttimeTier: 'reflectiveJournal',
    defaultMicroSession: 'diaphragmatic_breathing',
  },
];

export const PRESET_TARGETS_CATALOG: TargetItem[] = [
  // 1. Financial & Speculation
  {
    id: 'preset_fin_robinhood',
    category: 'financial_speculation',
    name: 'Robinhood',
    packageOrDomain: 'com.robinhood.android',
    alternativeDomain: 'robinhood.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['stocks', 'crypto', 'options', 'trading'],
  },
  {
    id: 'preset_fin_binance',
    category: 'financial_speculation',
    name: 'Binance',
    packageOrDomain: 'com.binance.dev',
    alternativeDomain: 'binance.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['crypto', 'bitcoin', 'futures'],
  },
  {
    id: 'preset_fin_coinbase',
    category: 'financial_speculation',
    name: 'Coinbase',
    packageOrDomain: 'com.coinbase.android',
    alternativeDomain: 'coinbase.com',
    isCustom: false,
    enabled: false,
    updatedAt: 1725000000000,
    tags: ['crypto', 'exchange'],
  },
  {
    id: 'preset_fin_draftkings',
    category: 'financial_speculation',
    name: 'DraftKings',
    packageOrDomain: 'com.draftkings.dknative',
    alternativeDomain: 'draftkings.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['sportsbook', 'betting', 'gambling'],
  },
  {
    id: 'preset_fin_polymarket',
    category: 'financial_speculation',
    name: 'Polymarket',
    packageOrDomain: 'polymarket.com',
    alternativeDomain: 'polymarket.com',
    isCustom: false,
    enabled: false,
    updatedAt: 1725000000000,
    tags: ['prediction', 'betting', 'crypto'],
  },

  // 2. Social Media & Infinite Feeds
  {
    id: 'preset_media_tiktok',
    category: 'mindful_media',
    name: 'TikTok',
    packageOrDomain: 'com.zhiliaoapp.musically',
    alternativeDomain: 'tiktok.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['video', 'reels', 'algorithm'],
  },
  {
    id: 'preset_media_instagram',
    category: 'mindful_media',
    name: 'Instagram',
    packageOrDomain: 'com.instagram.android',
    alternativeDomain: 'instagram.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['stories', 'reels', 'photos'],
  },
  {
    id: 'preset_media_x_twitter',
    category: 'mindful_media',
    name: 'X (Twitter)',
    packageOrDomain: 'com.twitter.android',
    alternativeDomain: 'x.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['feed', 'news', 'tweets'],
  },
  {
    id: 'preset_media_youtube',
    category: 'mindful_media',
    name: 'YouTube',
    packageOrDomain: 'com.google.android.youtube',
    alternativeDomain: 'youtube.com',
    isCustom: false,
    enabled: false,
    updatedAt: 1725000000000,
    tags: ['shorts', 'videos', 'streaming'],
  },
  {
    id: 'preset_media_reddit',
    category: 'mindful_media',
    name: 'Reddit',
    packageOrDomain: 'com.reddit.frontpage',
    alternativeDomain: 'reddit.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['forums', 'threads', 'infinite_scroll'],
  },

  // 3. Impulse Shopping & Flash Sales
  {
    id: 'preset_spend_amazon',
    category: 'mindful_spending',
    name: 'Amazon',
    packageOrDomain: 'com.amazon.mShop.android.shopping',
    alternativeDomain: 'amazon.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['shopping', '1click', 'cart'],
  },
  {
    id: 'preset_spend_shein',
    category: 'mindful_spending',
    name: 'Shein',
    packageOrDomain: 'com.zzkko',
    alternativeDomain: 'shein.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['fast_fashion', 'sales', 'deals'],
  },
  {
    id: 'preset_spend_temu',
    category: 'mindful_spending',
    name: 'Temu',
    packageOrDomain: 'com.einnovation.temu',
    alternativeDomain: 'temu.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['discounts', 'gamified_shopping'],
  },
  {
    id: 'preset_spend_ebay',
    category: 'mindful_spending',
    name: 'eBay',
    packageOrDomain: 'com.ebay.mobile',
    alternativeDomain: 'ebay.com',
    isCustom: false,
    enabled: false,
    updatedAt: 1725000000000,
    tags: ['auctions', 'marketplace'],
  },

  // 4. Late-Night Food Delivery
  {
    id: 'preset_eat_doordash',
    category: 'mindful_eating',
    name: 'DoorDash',
    packageOrDomain: 'com.dd.doordash',
    alternativeDomain: 'doordash.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['food_delivery', 'late_night', 'fast_food'],
  },
  {
    id: 'preset_eat_ubereats',
    category: 'mindful_eating',
    name: 'Uber Eats',
    packageOrDomain: 'com.ubercab.eats',
    alternativeDomain: 'ubereats.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['food_delivery', 'takeout'],
  },
  {
    id: 'preset_eat_grubhub',
    category: 'mindful_eating',
    name: 'Grubhub',
    packageOrDomain: 'com.grubhub.android',
    alternativeDomain: 'grubhub.com',
    isCustom: false,
    enabled: false,
    updatedAt: 1725000000000,
    tags: ['delivery', 'restaurants'],
  },
  {
    id: 'preset_eat_instacart',
    category: 'mindful_eating',
    name: 'Instacart',
    packageOrDomain: 'com.instacart.client',
    alternativeDomain: 'instacart.com',
    isCustom: false,
    enabled: false,
    updatedAt: 1725000000000,
    tags: ['groceries', 'snacks'],
  },

  // 5. Substance & Alcohol Reduction
  {
    id: 'preset_subst_drizly',
    category: 'substance_reduction',
    name: 'Drizly',
    packageOrDomain: 'com.drizly.delivery',
    alternativeDomain: 'drizly.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['alcohol', 'liquor_delivery'],
  },
  {
    id: 'preset_subst_saucey',
    category: 'substance_reduction',
    name: 'Saucey',
    packageOrDomain: 'com.saucey.saucey',
    alternativeDomain: 'saucey.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['alcohol_delivery', 'tobacco'],
  },
  {
    id: 'preset_subst_gopuff',
    category: 'substance_reduction',
    name: 'Gopuff',
    packageOrDomain: 'com.gobeer',
    alternativeDomain: 'gopuff.com',
    isCustom: false,
    enabled: false,
    updatedAt: 1725000000000,
    tags: ['late_delivery', 'convenience', 'alcohol'],
  },

  // 6. Digital Dependency & Screen-Time
  {
    id: 'preset_dig_twitch',
    category: 'digital_dependency',
    name: 'Twitch',
    packageOrDomain: 'tv.twitch.android.app',
    alternativeDomain: 'twitch.tv',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['livestreaming', 'gaming'],
  },
  {
    id: 'preset_dig_discord',
    category: 'digital_dependency',
    name: 'Discord',
    packageOrDomain: 'com.discord',
    alternativeDomain: 'discord.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['chat', 'servers', 'voice'],
  },
  {
    id: 'preset_dig_netflix',
    category: 'digital_dependency',
    name: 'Netflix',
    packageOrDomain: 'com.netflix.mediaclient',
    alternativeDomain: 'netflix.com',
    isCustom: false,
    enabled: false,
    updatedAt: 1725000000000,
    tags: ['binge_watching', 'shows', 'movies'],
  },
  {
    id: 'preset_dig_roblox',
    category: 'digital_dependency',
    name: 'Roblox',
    packageOrDomain: 'com.roblox.client',
    alternativeDomain: 'roblox.com',
    isCustom: false,
    enabled: false,
    updatedAt: 1725000000000,
    tags: ['gaming', 'metaverse'],
  },

  // 7. Adult Media & Explicit Content
  {
    id: 'preset_adult_onlyfans',
    category: 'adult_media',
    name: 'OnlyFans',
    packageOrDomain: 'onlyfans.com',
    alternativeDomain: 'onlyfans.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['adult', 'subscriptions', 'explicit'],
  },
  {
    id: 'preset_adult_fansly',
    category: 'adult_media',
    name: 'Fansly',
    packageOrDomain: 'fansly.com',
    alternativeDomain: 'fansly.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['adult', 'explicit'],
  },
  {
    id: 'preset_adult_chaturbate',
    category: 'adult_media',
    name: 'Chaturbate',
    packageOrDomain: 'chaturbate.com',
    alternativeDomain: 'chaturbate.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['adult', 'webcams'],
  },

  // 8. Gaming & Microtransactions
  {
    id: 'preset_game_steam',
    category: 'gaming_microtransactions',
    name: 'Steam Community & Store',
    packageOrDomain: 'com.valvesoftware.android.steam.community',
    alternativeDomain: 'store.steampowered.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['gaming', 'steam', 'microtransactions'],
  },
  {
    id: 'preset_game_roblox_store',
    category: 'gaming_microtransactions',
    name: 'Roblox Robux Store',
    packageOrDomain: 'com.roblox.client',
    alternativeDomain: 'roblox.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['gaming', 'robux', 'lootboxes'],
  },
  {
    id: 'preset_game_epic',
    category: 'gaming_microtransactions',
    name: 'Epic Games Store',
    packageOrDomain: 'epicgames.com',
    alternativeDomain: 'store.epicgames.com',
    isCustom: false,
    enabled: false,
    updatedAt: 1725000000000,
    tags: ['gaming', 'fortnite', 'vbucks'],
  },

  // 9. After-Hours Work & Burnout
  {
    id: 'preset_work_slack',
    category: 'after_hours_work',
    name: 'Slack',
    packageOrDomain: 'com.Slack',
    alternativeDomain: 'slack.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['work', 'messages', 'after_hours'],
  },
  {
    id: 'preset_work_jira',
    category: 'after_hours_work',
    name: 'Jira / Confluence',
    packageOrDomain: 'com.atlassian.android.jira.core',
    alternativeDomain: 'atlassian.net',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['work', 'tasks', 'burnout'],
  },
  {
    id: 'preset_work_gmail',
    category: 'after_hours_work',
    name: 'Work Email / Outlook',
    packageOrDomain: 'com.microsoft.office.outlook',
    alternativeDomain: 'outlook.office.com',
    isCustom: false,
    enabled: true,
    updatedAt: 1725000000000,
    tags: ['work', 'email', 'after_hours'],
  },

  // 10. Compulsive News & Panic Scrolling
  {
    id: 'preset_news_cnn',
    category: 'anxiety_news',
    name: 'CNN Breaking News',
    packageOrDomain: 'com.cnn.mobile.android.phone',
    alternativeDomain: 'cnn.com',
    isCustom: false,
    enabled: false,
    updatedAt: 1725000000000,
    tags: ['news', 'breaking', 'anxiety'],
  },
  {
    id: 'preset_news_nytimes',
    category: 'anxiety_news',
    name: 'The New York Times',
    packageOrDomain: 'com.nytimes.android',
    alternativeDomain: 'nytimes.com',
    isCustom: false,
    enabled: false,
    updatedAt: 1725000000000,
    tags: ['news', 'politics', 'scroll'],
  },
];

export function getDomainMeta(id: BehavioralDomainId): DomainMeta {
  const found = BEHAVIORAL_DOMAINS.find((d) => d.id === id);
  if (found) return found;
  return BEHAVIORAL_DOMAINS[0];
}

export function getPresetsForDomain(domainId: BehavioralDomainId): TargetItem[] {
  return PRESET_TARGETS_CATALOG.filter((p) => p.category === domainId);
}

export function getDefaultScheduledRules(): ScheduledStrictnessRule[] {
  return BEHAVIORAL_DOMAINS.map((domain) => ({
    categoryId: domain.id,
    daytimeTier: domain.defaultDaytimeTier,
    nighttimeTier: domain.defaultNighttimeTier,
    nightStartHour: 22, // 10:00 PM
    nightEndHour: 6,    // 06:00 AM
    allowOverride: domain.id !== 'adult_media' && domain.id !== 'substance_reduction',
    inactivityTimeoutMinutes: 15,
    continuousAllowanceMinutes: 20,
  }));
}

export function getDefaultTargets(): TargetItem[] {
  return PRESET_TARGETS_CATALOG.map((p) => ({ ...p }));
}

/**
 * Validates domain string, hostname, URL, or Android package name.
 * Returns true if valid, false if invalid.
 */
export function validateDomainOrPackage(input: string): {
  isValid: boolean;
  cleanIdentifier: string;
  errorMessage?: string;
} {
  const raw = (input || '').trim().toLowerCase();
  if (!raw) {
    return { isValid: false, cleanIdentifier: '', errorMessage: 'Input cannot be empty.' };
  }

  // Remove protocol and path if entered as URL (e.g. https://www.binance.com/trade -> binance.com)
  let clean = raw.replace(/^https?:\/\//, '').replace(/\/.*$/, '').trim();

  // Remove www. prefix if present
  clean = clean.replace(/^www\./, '');

  // Package name pattern: e.g. com.example.app (at least 2 segments, letters/numbers/underscores/dots)
  const packageRegex = /^[a-zA-Z][a-zA-Z0-9_]*(\.[a-zA-Z0-9_]+)+$/;

  // Domain name pattern: e.g. domain.com, sub.domain.co.uk
  const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

  if (packageRegex.test(clean) || domainRegex.test(clean)) {
    return { isValid: true, cleanIdentifier: clean };
  }

  return {
    isValid: false,
    cleanIdentifier: clean,
    errorMessage: 'Please enter a valid domain (e.g., example.com) or package ID (e.g., com.app.id).',
  };
}
